// components/molecules/FormGroup.tsx
import React, { FC, ReactNode } from 'react';
import { Label } from '@components/atoms/Label';

interface FormGroupProps {
  label?: string;
  htmlFor: string;
  helperText?: string;
  children: ReactNode;
  shouldShow?: boolean;
  error?: string;
  /** id of the error message node, for `aria-describedby`. Defaults to `${htmlFor}-error`. */
  errorId?: string;
}

export const FormGroup: FC<FormGroupProps> = ({
  label,
  error,
  errorId,
  htmlFor,
  children,
  helperText,
  shouldShow = true,
}) => {
  if (!shouldShow) return null;

  return (
    <div className="space-y-1 w-full">
      {label && <Label htmlFor={htmlFor} label={label} />}
      {helperText && (
        <p className="text-xs text-gray-500 dark:text-gray-400">{helperText}</p>
      )}
      <div className="w-full">
        {React.Children.map(children, (child) => {
          if (React.isValidElement(child)) {
            return React.cloneElement(child, {
              error: !!error,
              ...child.props,
            });
          }
          return child;
        })}
      </div>
      {error && (
        <p
          id={errorId ?? `${htmlFor}-error`}
          aria-live="polite"
          className="text-xs text-red-500 dark:text-red-400">
          {error}
        </p>
      )}
    </div>
  );
};
